import { useAppDispatch, useAppSelector } from '@app/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback, useMemo } from 'react';

import { useCreatorLabAnalytics } from '../../../routes/lab/hooks';
import { DEFAULT_TONE_OF_VOICE_ID, ToneOfVoiceOption } from '../domain';
import {
  selectContentComposerContentRepurposingState,
  selectContentComposerToneOfVoice,
  selectContentComposerToneOfVoiceConfig,
} from '../selector';
import { type SelectorOption, Selector } from './selector';

export const ToneOfVoiceSelector = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const contentRepurposing = useAppSelector(
    selectContentComposerContentRepurposingState,
  );
  const defaultToneOfVoice = useMemo(() => {
    if (
      contentRepurposing.status === 'success' &&
      contentRepurposing.data &&
      contentRepurposing.data.eligibility === 'ELIGIBLE' &&
      contentRepurposing.data.toneOfVoice &&
      contentRepurposing.data.toneOfVoice.status === 'FINISHED' &&
      contentRepurposing.data.toneOfVoice.value
    ) {
      return contentRepurposing.data.toneOfVoice.value;
    }
    return DEFAULT_TONE_OF_VOICE_ID;
  }, [contentRepurposing]);
  const toneOfVoiceConfig = useAppSelector(
    selectContentComposerToneOfVoiceConfig,
  );
  const selectedToneId = useAppSelector(selectContentComposerToneOfVoice);
  const { trackToneSelected } = useCreatorLabAnalytics();

  const toneOptions: ToneOfVoiceOption[] = useMemo(
    () =>
      toneOfVoiceConfig.status === 'success' && toneOfVoiceConfig.data
        ? toneOfVoiceConfig.data
        : [],
    [toneOfVoiceConfig],
  );

  const selectedTone = selectedToneId ?? '';

  const handleToneChange = useCallback(
    (toneId: string) => {
      const selectedToneOption = toneOptions.find(
        option => option.key === toneId,
      );
      const toneName = selectedToneOption?.toneOfVoice || toneId;
      trackToneSelected(toneName);
      dispatch({
        type: '[content-composer] tone selected',
        payload: { toneId },
      });
    },
    [dispatch, trackToneSelected, toneOptions],
  );

  const renderOption = useCallback(
    (option: SelectorOption) => {
      const isDefault = option.value === defaultToneOfVoice;
      return (
        <>
          <span className="truncate flex-1 text-left">{option?.label}</span>
          {isDefault && (
            <span className="text-spring-neutral-b4-muted ml-1 truncate">{`(${t(
              'content-hub.default',
            )})`}</span>
          )}
        </>
      );
    },
    [defaultToneOfVoice, t],
  );
  const renderSelected = useCallback(
    (option: SelectorOption) => {
      const isDefault = option.value === defaultToneOfVoice;
      return (
        <>
          <span className="truncate flex-1 text-left">{option?.label}</span>
          {isDefault && (
            <span className="text-spring-neutral-b4-muted ml-1 truncate">{`(${t(
              'content-hub.default',
            )})`}</span>
          )}
        </>
      );
    },
    [defaultToneOfVoice, t],
  );
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-medium leading-[1.43] text-white m-0">
          {t('content-recording.content-composer.tone-of-voice.title')}
        </h3>
        <p className="text-xs leading-[1.5] text-spring-neutral-b4-muted font-normal m-0">
          {t('content-recording.content-composer.tone-of-voice.description')}
        </p>
      </div>
      <Selector
        options={toneOptions.map(option => ({
          value: option.key,
          label: t(
            `content-recording.content-composer.tone-of-voice.options.${option.key}`,
            {
              defaultValue: option.toneOfVoice,
            },
          ),
        }))}
        selectedValue={selectedTone ?? ''}
        onChange={handleToneChange}
        dataTestId="tone-of-voice-selector"
        className="w-full"
        renderOption={renderOption}
        renderSelected={renderSelected}
      />
    </div>
  );
};
